import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, X } from "lucide-react";
import { toast } from "sonner";
import {
  getNotificationPreferences,
  getPushPublicKey,
  updateNotificationPreferences,
} from "@/api/client";
import {
  getWebPushAvailability,
  isWebPushSubscribed,
  requestWebPushPermission,
  subscribeToWebPush,
  unsubscribeFromWebPush,
} from "@/lib/webPush";

/**
 * Push onboarding — everyone meets notifications at first access, without
 * hunting through Settings:
 *
 * - Browser permission still "default" (the user never explicitly denied):
 *   a small card offers activation; the tap runs the NATIVE permission
 *   prompt (a user gesture is mandatory on iOS — an unprompted
 *   `Notification.requestPermission()` would be silently rejected there).
 * - Permission already granted but this device not subscribed (fresh
 *   login, cleared storage) and the push channel is on: re-subscribe
 *   silently — consent was already given.
 * - Permission denied, channel explicitly off, or "Non ora" dismissed:
 *   never nag (dismissal is per-device, `trace-push-onboarding`).
 */

const DISMISS_KEY = "trace-push-onboarding";

function dismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === "dismissed";
  } catch {
    return false;
  }
}

export function PushOnboardingBanner() {
  const queryClient = useQueryClient();
  const availability = getWebPushAvailability();
  const [hidden, setHidden] = useState(dismissed);
  const [busy, setBusy] = useState(false);
  const resubscribeAttempted = useRef(false);

  const eligible =
    availability.supported && !availability.requiresHomeScreen && !hidden;

  const publicKeyQuery = useQuery({
    queryKey: ["web-push", "public-key"],
    queryFn: getPushPublicKey,
    enabled: eligible,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const preferencesQuery = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: getNotificationPreferences,
    enabled: eligible,
    staleTime: 60_000,
    retry: 1,
  });
  const subscribedQuery = useQuery({
    queryKey: ["web-push", "browser-subscription"],
    queryFn: isWebPushSubscribed,
    enabled: eligible,
    staleTime: 0,
    retry: false,
  });

  const backendReady =
    publicKeyQuery.data?.enabled === true && !!publicKeyQuery.data.public_key;

  const enableMutation = useMutation({
    mutationFn: async () => {
      const publicKey = publicKeyQuery.data?.public_key;
      if (!backendReady || !publicKey) {
        throw new Error("Push delivery is not enabled on the TRACE server yet.");
      }
      const subscription = await subscribeToWebPush(publicKey);
      try {
        await updateNotificationPreferences({ channel_push: true });
        return subscription;
      } catch (error) {
        if (subscription.created)
          await unsubscribeFromWebPush().catch(() => undefined);
        throw error;
      }
    },
    onSuccess: async () => {
      setHidden(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["notification-preferences"] }),
        queryClient.invalidateQueries({ queryKey: ["web-push", "status"] }),
        queryClient.invalidateQueries({
          queryKey: ["web-push", "browser-subscription"],
        }),
      ]);
      toast.success("Notifiche push attive su questo dispositivo");
    },
    onError: (error) =>
      toast.error(
        error instanceof Error ? error.message : "Push setup failed",
      ),
  });

  // Silent device re-registration: permission granted + channel on, but this
  // browser has no subscription (fresh profile/login). No UI needed.
  useEffect(() => {
    if (
      !eligible ||
      resubscribeAttempted.current ||
      availability.permission !== "granted" ||
      !backendReady ||
      preferencesQuery.data?.channel_push !== true ||
      subscribedQuery.data !== false
    )
      return;
    resubscribeAttempted.current = true;
    subscribeToWebPush(publicKeyQuery.data!.public_key!)
      .then(() =>
        queryClient.invalidateQueries({
          queryKey: ["web-push", "browser-subscription"],
        }),
      )
      .catch(() => undefined);
  }, [
    eligible,
    availability.permission,
    backendReady,
    preferencesQuery.data?.channel_push,
    subscribedQuery.data,
    publicKeyQuery.data,
    queryClient,
  ]);

  const showCard =
    eligible &&
    availability.permission === "default" &&
    backendReady &&
    subscribedQuery.data === false &&
    // An explicit channel opt-out is a "no" — don't re-ask.
    preferencesQuery.data?.channel_push !== false;

  if (!showCard) return null;

  const dismiss = () => {
    setHidden(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, "dismissed");
    } catch {
      // in-memory dismissal still holds for this session
    }
  };

  const activate = async () => {
    // The native permission request MUST start inside the click stack
    // (Safari/iOS rejects it after an async boundary).
    const permissionRequest = requestWebPushPermission();
    setBusy(true);
    try {
      await permissionRequest;
      enableMutation.mutate();
    } catch (error) {
      // "denied" lands here too — respect it and stop asking.
      dismiss();
      toast.error(
        error instanceof Error ? error.message : "Notifiche non attivate",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-[calc(var(--tr-mobile-dock-clearance,0px)+12px)] left-3 right-3 z-[90] sm:bottom-4 sm:left-auto sm:right-4 sm:w-[340px]">
      <div className="card flex items-start gap-3 border-line-2 p-3.5 shadow-drawer">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-ds-sm bg-accent-soft text-accent">
          <BellRing className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">Attiva le notifiche</p>
          <p className="mt-0.5 text-xs leading-relaxed text-ink-3">
            Turni, cambi palco e avvisi operativi anche ad app chiusa.
          </p>
          <div className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={activate}
              disabled={busy || enableMutation.isPending}
              className="btn btn--accent btn--sm"
            >
              {busy || enableMutation.isPending ? "Attivo…" : "Attiva"}
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="btn btn--ghost btn--sm"
            >
              Non ora
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Chiudi"
          className="shrink-0 text-ink-4 transition-colors hover:text-ink-2"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
